import {AttrsSchema, Component} from 'panel';
import type {Instance as PopperInstance, Placement as PopperPlacement} from '@popperjs/core';
import {defaultModifiers, popperGenerator} from '@popperjs/core/lib/popper-lite';
import {default as popperFlipModifier} from '@popperjs/core/lib/modifiers/flip';
import {default as popperOffsetModifier} from '@popperjs/core/lib/modifiers/offset';
import {default as popperArrowModifier} from '@popperjs/core/lib/modifiers/arrow';
import {default as popperPreventOverflowModifier} from '@popperjs/core/lib/modifiers/preventOverflow';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import timing from 'common/stylesheets/mixins/timing.json';
import {CHROME_HEADER_HEIGHT} from 'common/constants';

import {
  DisplaySize,
  DisplayType,
  Placement,
  PositioningStrategy,
  TooltipAttrs,
  TooltipJsxProps,
  TooltipState,
} from './types';
import {template} from './template';
import css from './index.styl?inline';

const VISIBILITY_HIDDEN = `hidden`;
const VISIBILITY_TRANSITIONING = `transitioning`;
const VISIBILITY_VISIBLE = `visible`;

const createPopper = popperGenerator({
  defaultModifiers: [
    ...defaultModifiers,
    popperArrowModifier,
    popperOffsetModifier,
    popperPreventOverflowModifier,
    popperFlipModifier,
  ],
});

// defaults right and left positioning to popper's top edge alignment
const POPPER_POSITION_OVERRIDES: {[key in Placement]: PopperPlacement} = {
  [Placement.Left]: `left-start`,
  [Placement.Right]: `right-start`,
  [Placement.Top]: `top`,
  [Placement.Bottom]: `bottom`,
};

/**
 * When a delay is provided, after a tooltip is shown with initial in-delay, hovering over neighbor
 * elements with tooltips (while the previous tooltip is still shown with an out-delay or not) will not have a delay effect.
 *
 * This behavior is achieved by keeping a reference to the currently active tooltip so the next tooltip knows it's part of a "chain",
 * and hide the previous tooltip when it has an out-delay
 */
let currentlyActiveTooltip: Tooltip = null;

export class Tooltip extends Component<TooltipState, TooltipAttrs> {
  private _listeners;
  private _parentEl;
  private _pendingPositionTooltipRaf;
  private _pendingPositionTooltipTimeout;
  private _pendingHideTooltipTimeout;
  private elmRefs: Record<string, HTMLDivElement> = {};

  private popperInstance: PopperInstance = null;

  static get attrsSchema(): AttrsSchema<TooltipAttrs> {
    return {
      ...super.attrsSchema,
      'always-show': `boolean`,
      offset: {type: `number`, default: 8},
      'max-width': {type: `string`, default: null},
      placement: {type: `string`, default: Placement.Top, enum: Object.values(Placement)},
      type: {type: `string`, default: DisplayType.Default, enum: Object.values(DisplayType)},
      'delay-in': {
        type: `number`,
        description: `value in ms to delay showing tooltip`,
        default: timing[`$mp-default-tooltip-delay-in`],
      },
      'delay-out': {
        type: `number`,
        description: `value in ms to delay hiding tooltip`,
        default: timing[`$mp-default-tooltip-delay-out`],
      },
      'image-src': {type: `string`, description: `image to show on top of tooltip, used for tutorials.`},
      size: {type: `string`, enum: Object.values(DisplaySize), default: DisplaySize.Fit},
      text: {type: `string`, description: `display formatted text with links inside the tooltip.`},
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Fixed,
        description: `Defines how the tooltip is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
    };
  }

  constructor() {
    super();
    // track added listeners so we can remove them in disconnectedCallback and be GC friendly
    this._listeners = new Map();
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        visibility: VISIBILITY_HIDDEN,
        isFirstActiveTooltip: true,
      },
      helpers: {
        setElmRef: (name: string) => ({elm}) => (this.elmRefs[name] = elm),
        VISIBILITY_HIDDEN,
        VISIBILITY_TRANSITIONING,
        VISIBILITY_VISIBLE,
        stopPropagation: (ev) => {
          ev.stopPropagation();
        },
        getTooltipEl: (): HTMLDivElement | null => this.elmRefs[`main`] || this.el.querySelector(`.mp-tooltip-main`),
        shouldAnimateTooltip: () => !!this.attr(`delay-in`) && this.state.isFirstActiveTooltip,
      },
    };
  }

  /**
   * Setups the positioning library for the tooltip, for more info see:
   * https://popper.js.org/docs/v2/
   */
  setupPopper() {
    // initializes popper for the first time the tooltip is being displayed
    !this.popperInstance && (this.popperInstance = createPopper(this.parentEl, this));

    this.popperInstance.setOptions({
      placement: POPPER_POSITION_OVERRIDES[this.attr(`placement`)],
      strategy: this.attr(`position-strategy`),
      modifiers: [
        // moves the tooltip in a way that prevents it
        // from being cut from the viewport
        {
          name: `preventOverflow`,
          options: {
            padding: 8,
          },
        },
        // moves the tooltip so that it distances itself
        // from its parent, accounts for the tooltip arrow
        {
          name: `offset`,
          options: {
            offset: [0, this.attr(`offset`)],
          },
        },
        // aligns the tooltip's arrow to the center of
        // the tooltip's parent, both horizontally
        // and vertically depending on how the tooltip
        // is positioned
        {
          name: `arrow`,
          options: {
            padding: 4, // distance from tooltip's edge to arrow
            element: this.el.querySelector(`.mp-tooltip-arrow`),
          },
        },
        // makes the tooltip align itself so that it
        // is visible inside the window viewport
        {
          name: `flip`,
          options: {
            rootBoundary: `viewport`,
            flipVariations: false,
            padding: {top: CHROME_HEADER_HEIGHT},
          },
        },
      ],
    });

    return this.popperInstance;
  }

  show() {
    // Avoid race condition when mouse leaves and enters within the delay window
    if (currentlyActiveTooltip === this) {
      return;
    }

    this.setupPopper();
    this.update({
      visibility: VISIBILITY_TRANSITIONING,
    });

    const delay = this.attr(`delay-in`);
    const makeTooltipVisible = () => {
      if (delay && !this.attr(`always-show`)) {
        this.update({isFirstActiveTooltip: !currentlyActiveTooltip});
        currentlyActiveTooltip?.hideAndCancelPositioning();
        currentlyActiveTooltip = this;
      }

      this.update({
        visibility: VISIBILITY_VISIBLE,
      });

      this.popperInstance.update();
    };

    if (delay && !currentlyActiveTooltip) {
      this._pendingPositionTooltipTimeout = window.setTimeout(() => {
        makeTooltipVisible();
      }, delay);
    } else {
      this._pendingPositionTooltipRaf = window.requestAnimationFrame(() => {
        makeTooltipVisible();
      });
    }
  }

  hide() {
    this.cancelPendingPositionTooltipIfNeeded();

    const delay = this.attr(`delay-out`);
    const hideTooltip = () => {
      this.update({visibility: VISIBILITY_HIDDEN});
      currentlyActiveTooltip = null;
      // pauses popper positioning logic when tooltip is hidden
      this.popperInstance?.setOptions({
        modifiers: [{name: `eventListeners`, enabled: false}],
      });
    };
    if (delay) {
      this._pendingHideTooltipTimeout = setTimeout(() => hideTooltip(), delay);
    } else {
      hideTooltip();
    }
  }

  /**
   * Used for adding/removing event listeners
   */
  get parentEl() {
    return this._parentEl || this.parentNode;
  }

  set parentEl(parentEl) {
    this._parentEl = parentEl;
  }

  connectedCallback() {
    super.connectedCallback();

    // From MDN doc https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements
    // "connectedCallback may be called once your element is no longer connected, use
    // Node.isConnected to make sure."
    // This is happening when dragging query entry rows in the query builder. As a result,
    // parentNode is unexpectedly null, leading to a JS exception.
    //
    // Secondary check for this.parentNode existing because it is intermittently null in iso tests as of panel@5.7.0
    // which fixed setting isConnected before connectedCallback(). TODO(jakub): investigate why this is happening.
    if (!this.isConnected || !this.parentNode) {
      return;
    }

    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);

    this.render();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (this.initialized) {
      this.render();
    }
  }

  render() {
    this.removeListenersIfNeeded();
    if (this.attr(`always-show`)) {
      this.show();
    } else {
      if (this.state.visibility === VISIBILITY_VISIBLE) {
        this.show();
      }
      this._addEventListener(this.parentEl, `mouseenter`, this.show);
      this._addEventListener(this.parentEl, `mouseleave`, this.hide);
    }
  }

  _addEventListener(el, event, listener) {
    el.addEventListener(event, listener);
    if (!this._listeners.has(el)) {
      this._listeners.set(el, {[event]: listener});
    } else {
      this._listeners.get(el)[event] = listener;
    }
  }

  removeListenersIfNeeded() {
    for (const [el, listeners] of this._listeners.entries()) {
      for (const [event, listener] of Object.entries(listeners)) {
        el.removeEventListener(event, listener);
      }
    }
  }

  cancelPendingPositionTooltipIfNeeded() {
    if (this._pendingPositionTooltipRaf) {
      window.cancelAnimationFrame(this._pendingPositionTooltipRaf);
      this._pendingPositionTooltipRaf = null;
    }

    if (this._pendingPositionTooltipTimeout) {
      window.clearTimeout(this._pendingPositionTooltipTimeout);
      this._pendingPositionTooltipTimeout = null;
    }

    if (this._pendingHideTooltipTimeout) {
      window.clearTimeout(this._pendingHideTooltipTimeout);
      this._pendingHideTooltipTimeout = null;
    }
  }

  disconnectedCallback() {
    this.popperInstance?.destroy();
    super.disconnectedCallback();
    this.removeListenersIfNeeded();
    this.cancelPendingPositionTooltipIfNeeded();
    if (currentlyActiveTooltip === this) {
      currentlyActiveTooltip = null;
    }
  }

  // Called externally by reference to this component
  public hideAndCancelPositioning() {
    this.update({visibility: VISIBILITY_HIDDEN});
    this.cancelPendingPositionTooltipIfNeeded();
  }
}

export const TooltipEl = defineAndMakeJsxFactory<TooltipJsxProps>(`mp-tooltip`, Tooltip);
